// frontend/src/features/ogdb2/components/ColumnMapperModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import { OGDB2_METRICS } from "../metricRegistry";

function norm(s) {
  return String(s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

/**
 * Firm defaults:
 * - keyed by firm value used in ImportExportPanel (capitol, pritchard_abbott, wardlaw, thomas_y_pickett)
 * - then by fileType (lease_summary, appraisal_roll)
 * - finally: mapping of raw header -> metric_code
 */
const FIRM_DEFAULTS = {
  capitol: {
    lease_summary: {
      // From your screenshot (Capitol Lease Summary)
      ClientName: "appraisal_district",
      CompositeRateOfReturn: "discount_rate",
      "Oil Discount Rate": "oil_discount_rate",
      "Gas Discount Rate": "gas_discount_rate",

      FirstYearGasAnnualProduction: "gas_mcf",
      Gas1ProjectionStartRate: "gas_start_rate",
      FirstYearOilAnnualProduction: "oil_bbl",
      Oil1ProjectionStartRate: "oil_start_rate",

      FirstYearOperatorCost: "loe",

      Gas1DeclineDeclineRate: "gas_decline_rate",
      Gas1DeclineNfactor: "gas_decline_n_factor",

      GasPrice: "gas_price",
      GasReservesEconomic: "gas_reserves_economic",

      LeaseName: "lease_name",
      LeaseNumber: "lease_number",

      LeaseRoyaltyInterestFraction: "royalty_interest_pct",
      LeaseWorkingInterestFraction: "working_interest_pct",

      LifeOfPropertyEconomic: "economic_life_years",
      NetEquipmentValue: "net_equipment_value",
      NumberOfWells: "number_of_wells",

      Oil1DeclineDeclineRate: "oil_decline_rate",
      Oil1DeclineNfactor: "oil_decline_n_factor",

      OilPrice: "oil_price",
      OilReservesEconomic: "oil_reserves_economic",

      ProductCd: "product_cd",

      RoyaltyInterestValue: "royalty_interest_value",
      WorkingInterestValue: "working_interest_value",
    },
    appraisal_roll: {
      LeaseNumber: "lease_number",
      LeaseName: "lease_name",

      Taxable_CurrentYear: "taxable_current_year",
      Taxable_PriorYear: "taxable_prior_year",
      Market_CurrentYear: "market_current_year",
      Market_PriorYear: "market_prior_year",

      Jurisdictions: "taxing_jurisdictions",
      LegalDescription: "legal_description",
    },
  },

  // GasSearch is independent of firm; default map is keyed by fileType=gassearch
  gassearch: {
    gassearch: {
      // Totals
      "RRC Crude Bbl": "oil_bbl",
      "RRC Gas MCF": "gas_mcf",

      // ✅ Pricing (new export format) — default to Sales pricing
      "Sales $/Bbl": "oil_price",
      "Sales $/MCF": "gas_price",

      // Fallback legacy columns (still supported if present)
      "O St-Non-Wgtd": "oil_price",
      "G St-Non-Wgtd": "gas_price",

      // Values
      "Crude Lease Value": "oil_lease_value",
      "Gas Lease Value": "gas_lease_value",

      // Identity helpers
      "Lease Name": "lease_name",
      "RRC #": "lease_number",
    },
  },

  pritchard_abbott: {
    lease_summary: {
      // P&A Lease Summary → canonical OGDB2 metric codes
      // Anchor (equivalent to Capitol LeaseNumber)
      "RRC#": "lease_number",
      "RRC #": "lease_number",
      RRC: "lease_number",
      "LEASE #": "lease_number",

      "LEASE NAME": "lease_name",
      "OIL OR GAS": "product_cd",

      // Interests (fractions)
      "WI DECIMAL": "working_interest_pct",
      "RI DECIMAL": "royalty_interest_pct",

      // Prices
      "OIL WELLHEAD PRICE": "oil_price",
      "GAS WI GR PRC": "gas_price",

      // Expenses
      "OP EXP $/YR": "loe",

      // Start rates
      "OIL BOPD": "oil_start_rate",
      "GAS MCF/D": "gas_start_rate",

      // Discount
      DISC: "discount_rate",

      // Values (prior + current)
      "PRVYR WI VAL": "prior_year_working_interest_value",
      "PRVYR RI VAL": "prior_year_royalty_interest_value",
      "WI TOTAL VAL": "working_interest_value",
      "RI TOTAL VAL": "royalty_interest_value",

      // Equipment
      "EQUIP VAL": "net_equipment_value",

      // Reserves
      "GAS RESERVES": "gas_reserves_economic",
      "OIL RESERVES": "oil_reserves_economic",

      // Declines
      "OIL DECL#1": "oil_decline_rate",
      "GAS DECL#1": "gas_decline_rate",

      // ✅ Economic life + hyperbolic factor (enables decline asks rules)
      "YRS MAX": "economic_life_years",
      "OIL HYPERBOLIC FACT": "oil_decline_n_factor",
      "GAS HYPERBOLIC FACT": "gas_decline_n_factor",

      // Counts
      "#WELLS": "number_of_wells",
    },
    appraisal_roll: {},
  },

  // ✅ Wardlaw Appraisal Group
  wardlaw: {
    lease_summary: {
      // Identity
      RrcLease: "lease_number", // ✅ lease number anchor
      LpdLeaseName: "lease_name", // combined display name handled in importer; this is still useful
      Type: "product_cd",

      // Ownership
      WIPct: "working_interest_pct",

      // ✅ Value buckets (Wardlaw provides both splits directly)
      Cur78: "working_interest_value",
      Cur18: "royalty_interest_value",

      // Prices
      PriceGas: "gas_price",
      PriceOil: "oil_price",

      // Production (Year 1)
      Yr1OilProd: "oil_bbl",
      Yr1GasProd: "gas_mcf",

      // Reserves
      ReserveOil: "oil_reserves_economic",
      ReserveGas: "gas_reserves_economic",

      // Expenses
      Loe: "loe",
      ProdCost: "opex",

      // Discount / life / equip / wells
      DiscountRate: "discount_rate",
      maxLife: "economic_life_years",
      ValEqpt: "net_equipment_value",
      WellCnt: "number_of_wells",

      // NOTE: Qi/QiSec and decline fields are product-dependent in Wardlaw (Type=OIL/GAS),
      // so we leave them unmapped by default to avoid wrong auto-maps.
    },
    appraisal_roll: {},
  },
};

function getFirmDefaultMap({ firm, fileType }) {
  const f = String(firm || "").trim().toLowerCase();
  const ft = String(fileType || "").trim().toLowerCase();

  // GasSearch is independent of firm selection
  if (ft === "gassearch" || ft === "gas_search") {
    return FIRM_DEFAULTS?.gassearch?.gassearch || null;
  }

  return FIRM_DEFAULTS?.[f]?.[ft] || null;
}

function guessMetricCode(rawHeader) {
  const n = norm(rawHeader);

  // direct "best effort" keyword guessing
  if (n.includes("lease number")) return "lease_number";
  if (n === "lease") return "lease_name";
  if (n.includes("lease name")) return "lease_name";

  if (n.includes("working interest")) return "working_interest_pct";
  if (n.includes("royalty interest")) return "royalty_interest_pct";

  if (n.includes("oil price")) return "oil_price";
  if (n.includes("gas price")) return "gas_price";

  if (n.includes("oil") && n.includes("bbl")) return "oil_bbl";
  if (n.includes("gas") && (n.includes("mcf") || n.includes("mcfs"))) return "gas_mcf";

  if (n.includes("discount rate")) return "discount_rate";
  if (n.includes("economic life")) return "economic_life_years";

  if (n.includes("market") && n.includes("current")) return "market_current_year";
  if (n.includes("market") && n.includes("prior")) return "market_prior_year";
  if (n.includes("taxable") && n.includes("current")) return "taxable_current_year";
  if (n.includes("taxable") && n.includes("prior")) return "taxable_prior_year";

  if (n.includes("jurisdiction")) return "taxing_jurisdictions";
  if (n.includes("legal")) return "legal_description";

  return null;
}

function stringifyCell(v) {
  if (v === null || v === undefined) return "";
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

export default function ColumnMapperModal({
  open,
  preview,
  client,
  fileType,
  firm,
  initialMapping,
  onClose,
  onSaveAndContinue,
}) {
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedRaw, setSelectedRaw] = useState(""); // drives the right-side preview panel

  const rawCols = useMemo(() => {
    // ✅ Backend preview uses columns_raw (see BAIDImportPreviewModal)
    const fromApi = Array.isArray(preview?.columns_raw)
      ? preview.columns_raw.map((c) => String(c || "").trim()).filter(Boolean)
      : [];

    if (fromApi.length) return fromApi;

    // ✅ Fallback: derive from first preview row raw keys
    const rowsPrev = Array.isArray(preview?.rows_preview) ? preview.rows_preview : [];
    const firstRaw = rowsPrev.length ? rowsPrev[0]?.raw : null;

    if (firstRaw && typeof firstRaw === "object" && !Array.isArray(firstRaw)) {
      return Object.keys(firstRaw).map((k) => String(k || "").trim()).filter(Boolean);
    }

    return [];
  }, [preview?.columns_raw, preview?.rows_preview]);

  const metricsIndex = useMemo(() => {
    const map = new Map();
    for (const m of OGDB2_METRICS || []) map.set(m.code, m);
    return map;
  }, []);

  // Build sample values for each raw column from preview rows (unique, first few)
  const rawSamples = useMemo(() => {
    const out = new Map();
    const rowsPrev = Array.isArray(preview?.rows_preview) ? preview.rows_preview : [];
    const maxRowsToScan = 200; // keep it fast
    const maxSamplesPerCol = 3;

    for (const col of rawCols) out.set(col, []);

    for (let i = 0; i < rowsPrev.length && i < maxRowsToScan; i++) {
      const r = rowsPrev[i];
      const rawObj = r?.raw && typeof r.raw === "object" && !Array.isArray(r.raw) ? r.raw : null;
      if (!rawObj) continue;

      for (const col of rawCols) {
        const arr = out.get(col);
        if (!arr || arr.length >= maxSamplesPerCol) continue;

        const v = stringifyCell(rawObj[col]);
        const cleaned = String(v || "").trim();
        if (!cleaned) continue;

        // enforce uniqueness (case-insensitive)
        const exists = arr.some((x) => String(x).toLowerCase() === cleaned.toLowerCase());
        if (!exists) arr.push(cleaned);
      }
    }

    return out;
  }, [preview?.rows_preview, rawCols]);

  // For the right-side panel: first N row values for selected column
  const selectedPreviewRows = useMemo(() => {
    const rowsPrev = Array.isArray(preview?.rows_preview) ? preview.rows_preview : [];
    if (!selectedRaw) return [];
    const out = [];
    const max = 25;

    for (let i = 0; i < rowsPrev.length && out.length < max; i++) {
      const r = rowsPrev[i];
      const rawObj = r?.raw && typeof r.raw === "object" && !Array.isArray(r.raw) ? r.raw : null;
      if (!rawObj) continue;

      const val = stringifyCell(rawObj[selectedRaw]);
      out.push({ idx: r?.row_index ?? i, val: String(val ?? "") });
    }

    return out;
  }, [preview?.rows_preview, selectedRaw]);

  useEffect(() => {
    if (!open) return;

    const initial = Array.isArray(initialMapping) ? initialMapping : [];

    const ft = String(fileType || "").trim().toLowerCase();
    const defaultScenario = ft === "gassearch" || ft === "gas_search" ? "third_party" : "assessor";

    // 1) Saved template always wins if present
    const mapByRaw = new Map();
    for (const it of initial) {
      if (!it || typeof it !== "object") continue;
      const raw = String(it.raw || "").trim();
      if (!raw) continue;
      mapByRaw.set(raw, {
        raw,
        metric_code: String(it.metric_code || "").trim(),
        scenario: String(
          it.scenario ||
            (String(fileType || "").trim().toLowerCase() === "gassearch" ? "third_party" : "assessor")
        )
          .trim()
          .toLowerCase(),
        enabled: true,
      });
    }

    const hasSavedTemplate = initial.length > 0;

    // Track used metric codes to prevent duplicate *auto* guesses.
    // (Saved template can contain duplicates; we don't touch user choices.)
    const used = new Set();
    if (hasSavedTemplate) {
      for (const v of mapByRaw.values()) {
        if (v.metric_code) used.add(v.metric_code);
      }
    }

    // 2) If no saved template, apply firm defaults (also 1:1, no duplicates)
    if (!hasSavedTemplate) {
      const defaults = getFirmDefaultMap({ firm, fileType });
      if (defaults) {
        for (const raw of rawCols) {
          const m = defaults[raw];
          if (!m) continue;
          if (mapByRaw.has(raw)) continue;
          if (used.has(m)) continue; // protect against accidental duplicates in defaults
          mapByRaw.set(raw, {
            raw,
            metric_code: m,
            scenario: defaultScenario,
            enabled: true,
          });
          used.add(m);
        }
      }
    }

    // 3) Build final rows list; if still unmapped, use guess (but never duplicate)
    const built = rawCols.map((raw) => {
      const existing = mapByRaw.get(raw);
      if (existing) return existing;

      const guess = guessMetricCode(raw);
      if (guess && !used.has(guess)) {
        used.add(guess);
        return {
          raw,
          metric_code: guess,
          scenario: defaultScenario,
          enabled: true,
        };
      }

      // leave as not mapped; user can decide
      return {
        raw,
        metric_code: "",
        scenario: defaultScenario,
        enabled: false,
      };
    });

    setRows(built);

    // If nothing selected yet, select the first column so the preview panel isn't empty
    if (!selectedRaw && rawCols.length) setSelectedRaw(rawCols[0]);
  }, [open, rawCols, initialMapping, firm, fileType]); // keep behavior identical

  const filtered = useMemo(() => {
    const q = norm(search);
    if (!q) return rows;
    return rows.filter((r) => {
      const h = norm(r.raw);
      const m = norm(r.metric_code);
      return h.includes(q) || m.includes(q);
    });
  }, [rows, search]);

  async function saveAndContinue() {
    setErr("");
    setSaving(true);
    try {
      const payload = rows.map((r) => ({
        raw: String(r.raw || "").trim(),
        metric_code: String(r.metric_code || "").trim(),
        scenario: String(r.scenario || "").trim().toLowerCase(),
        enabled: !!r.enabled,
      }));

      // Let parent handle persistence (template save etc.)
      await onSaveAndContinue(payload);
    } catch (e) {
      setErr(e?.message || "Failed to save mapping.");
    } finally {
      setSaving(false);
    }
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-6xl rounded-lg shadow-lg border border-gray-200 overflow-hidden">
        <div className="p-4 border-b border-gray-200 flex items-start justify-between gap-3">
          <div>
            <div className="text-lg font-semibold text-gray-900">Map Columns</div>
            <div className="text-xs text-gray-500 mt-1">
              Client: <span className="font-semibold">{client || "-"}</span> • Firm:{" "}
              <span className="font-semibold">{firm || "-"}</span> • File Type:{" "}
              <span className="font-semibold">{fileType || "-"}</span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              className="px-3 py-2 text-sm rounded border border-gray-300 hover:bg-gray-50"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              className="px-3 py-2 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
              onClick={saveAndContinue}
              disabled={saving}
            >
              {saving ? "Saving..." : "Save & Continue"}
            </button>
          </div>
        </div>

        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <input
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
              placeholder="Search raw headers or metric codes..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {err ? <div className="mt-2 text-sm text-red-600">{err}</div> : null}
        </div>

        {/* Body: table + preview panel */}
        <div className="flex max-h-[70vh]">
          {/* Left: mapping table */}
          <div className="flex-1 overflow-auto border-r border-gray-200">
            <table className="min-w-full text-sm">
              <thead className="sticky top-0 bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold text-gray-700 w-[26%]">Raw Header</th>
                  <th className="text-left px-3 py-2 font-semibold text-gray-700 w-[20%]">Sample values</th>
                  <th className="text-left px-3 py-2 font-semibold text-gray-700 w-[28%]">Metric Code</th>
                  <th className="text-left px-3 py-2 font-semibold text-gray-700 w-[16%]">Scenario</th>
                  <th className="text-left px-3 py-2 font-semibold text-gray-700 w-[10%]">Use?</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, idx) => {
                  const def = metricsIndex.get(r.metric_code);
                  const samples = rawSamples.get(r.raw) || [];
                  const isSelected = selectedRaw === r.raw;

                  return (
                    <tr
                      key={`${r.raw}-${idx}`}
                      className={`border-b border-gray-100 hover:bg-gray-50 ${isSelected ? "bg-blue-50/50" : ""}`}
                    >
                      <td className="px-3 py-2 align-top">
                        <button
                          type="button"
                          className="text-left w-full"
                          onClick={() => setSelectedRaw(r.raw)}
                          title="Click to preview this column"
                        >
                          <div className="font-medium text-gray-900">{r.raw}</div>
                          <div className="text-[11px] text-gray-500 mt-0.5">Click to preview →</div>
                        </button>
                      </td>

                      <td className="px-3 py-2 align-top">
                        {samples.length ? (
                          <div className="text-xs text-gray-700 space-y-1">
                            {samples.map((s, i) => (
                              <div
                                key={`${r.raw}-s-${i}`}
                                className="truncate max-w-[260px]"
                                title={s}
                              >
                                {s}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-xs text-gray-400">(no sample)</div>
                        )}
                      </td>

                      <td className="px-3 py-2 align-top">
                        <select
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm bg-white"
                          value={r.metric_code}
                          onChange={(e) => {
                            const v = e.target.value;
                            setRows((prev) =>
                              prev.map((x) => (x.raw === r.raw ? { ...x, metric_code: v, enabled: !!v } : x))
                            );
                          }}
                        >
                          <option value="">(not mapped)</option>
                          {(OGDB2_METRICS || []).map((m) => (
                            <option key={m.code} value={m.code}>
                              {m.code} — {m.label}
                            </option>
                          ))}
                        </select>
                        {def ? <div className="text-xs text-gray-500 mt-1">{def.label}</div> : null}
                      </td>

                      <td className="px-3 py-2 align-top">
                        <select
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm bg-white"
                          value={r.scenario}
                          onChange={(e) => {
                            const v = e.target.value;
                            setRows((prev) => prev.map((x) => (x.raw === r.raw ? { ...x, scenario: v } : x)));
                          }}
                        >
                          <option value="assessor">assessor</option>
                          <option value="actual">actual</option>
                          <option value="third_party">third_party</option>
                          <option value="ba">ba</option>
                        </select>
                      </td>

                      <td className="px-3 py-2 align-top">
                        <input
                          type="checkbox"
                          checked={!!r.enabled}
                          onChange={(e) => {
                            const v = e.target.checked;
                            setRows((prev) => prev.map((x) => (x.raw === r.raw ? { ...x, enabled: v } : x)));
                          }}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Right: data preview */}
          <div className="w-[360px] shrink-0 overflow-auto">
            <div className="p-3 border-b border-gray-200">
              <div className="text-sm font-semibold text-gray-900">Column Preview</div>
              <div className="text-xs text-gray-500 mt-1">
                Showing values for:{" "}
                <span className="font-semibold text-gray-800">{selectedRaw || "(none)"}</span>
              </div>
            </div>

            <div className="p-3">
              {selectedRaw ? (
                <>
                  <div className="text-xs text-gray-500 mb-2">
                    First {selectedPreviewRows.length} rows (raw):
                  </div>

                  <div className="space-y-2">
                    {selectedPreviewRows.map((r) => (
                      <div key={`pv-${r.idx}`} className="rounded border border-gray-200 p-2">
                        <div className="text-[11px] text-gray-500">row {r.idx}</div>
                        <div className="text-sm text-gray-900 break-words">
                          {r.val || <span className="text-gray-400">(blank)</span>}
                        </div>
                      </div>
                    ))}
                  </div>

                  {!selectedPreviewRows.length ? (
                    <div className="text-sm text-gray-500">(No preview rows available.)</div>
                  ) : null}
                </>
              ) : (
                <div className="text-sm text-gray-500">Click a raw header to preview its data.</div>
              )}
            </div>
          </div>
        </div>

        <div className="p-4 border-t border-gray-200 text-xs text-gray-500">
          Tip: Saved mappings will apply automatically for the same firm + file type next time.
        </div>
      </div>
    </div>
  );
}